
import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Input, Label, Row, Button, Form, FormFeedback, Spinner } from 'reactstrap';
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import withRouter from "../../Components/Common/withRouter";
import * as Yup from "yup";
import { useFormik } from "formik";
import { loginUser, resetLoginFlag } from "../../slices/auth/login/thunk";
import { createSelector } from 'reselect';
import { useToast } from '../../Components/Hooks/useToast';
import logo from "../../assets/images/xenthr.png";

// Company constants
const COMPANY_NAME = process.env.REACT_APP_COMPANY_TITLE || 'VSR (P) LTD.,';
const DEFAULT_THEME_COLOR = '#cee76a'; // Blue theme

const Login = (props: any) => {
    const dispatch: any = useDispatch();
    const toast = useToast();
    const [passwordShow, setPasswordShow] = useState<boolean>(false);
    const [logoError, setLogoError] = useState<boolean>(false);

    // Redux selectors
    const selectLayoutState = (state: any) => state;
    const loginpageData = createSelector(
        selectLayoutState,
        (state) => ({
            user: state.login?.user || {},
            error: state.login?.error || '',
            errorMsg: state.login?.errorMsg || false,
            loading: state.login?.loading || false,
            isUserLogout: state.login?.isUserLogout || false,
        })
    );

    const { user, error, errorMsg, loading, isUserLogout } = useSelector(loginpageData);

    // Company config
    const companyConfig = {
        name: COMPANY_NAME,
        theme_color: DEFAULT_THEME_COLOR,
        logo: logo,
        zone_image: null,
    };

    // Show error toast
    useEffect(() => {
        if (error && errorMsg) {
            toast.error(error, {
                className: 'bg-danger text-white',
                autoClose: 8000,
            });

            setTimeout(() => {
                dispatch(resetLoginFlag());
            }, 3000);
        }
    }, [error, errorMsg, toast, dispatch]);

    // Show success toast on login
    useEffect(() => {
        if (user && Object.keys(user).length > 0 && !errorMsg && !loading) {
            toast.success('Login successful! Redirecting...', {
                className: 'bg-success text-white',
                autoClose: 3000,
            });
        }
    }, [user, errorMsg, loading, toast]);

    // Show logout toast
    useEffect(() => {
        if (isUserLogout) {
            toast.info('You have been logged out successfully.', {
                className: 'bg-info text-white',
                autoClose: 4000,
            });
        }
    }, [isUserLogout, toast]);

    // Form validation
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            username: '',
            password: '',
        },
        validationSchema: Yup.object({
            username: Yup.string().required("Please enter your username or email"),
            password: Yup.string().required("Please enter your password"),
        }),
        onSubmit: async (values) => {
            const loadingToastId = toast.loading('Signing in...', {
                className: 'bg-primary text-white',
                hideProgressBar: true,
            });

            try {
                const loginData = {
                    login: values.username,
                    password: values.password
                };

                await dispatch(loginUser(loginData, props.router.navigate));

                toast.update(loadingToastId, {
                    render: 'Login successful! Redirecting...',
                    type: 'success',
                    className: 'bg-success text-white',
                    autoClose: 3000,
                    hideProgressBar: false,
                });

            } catch (err: any) {
                toast.update(loadingToastId, {
                    render: err.message || 'Login failed. Please try again.',
                    type: 'error',
                    className: 'bg-danger text-white',
                    autoClose: 8000,
                    hideProgressBar: false,
                });
            }
        }
    });

    return (
        <React.Fragment>
            <ParticlesAuth
                themeColor={companyConfig.theme_color}
                zoneImage={companyConfig.zone_image}
            >
                <div className="auth-page-content mt-lg-5">
                    <Container>
                        <Row>
                            <Col lg={12}>
                                <div className="text-center mt-sm-5 mb-4 text-white-50">
                                    <div>
                                        <Link to="#" className="d-inline-block auth-logo">
                                            <img
                                                src={logo}
                                                alt={COMPANY_NAME}
                                                height="80"
                                                onError={() => setLogoError(true)}
                                            />
                                        </Link>
                                    </div>
                                    <p className="mt-3 fs-15 fw-medium">{COMPANY_NAME}</p>
                                    {logoError && (
                                        <small className="text-warning">
                                            Logo not available
                                        </small>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        <Row className="justify-content-center">
                            <Col md={8} lg={6} xl={5}>
                                <Card className="mt-4 card-bg-fill">
                                    <CardBody className="p-4">
                                        <div className="text-center mt-2">
                                            <h5 className="text-primary">Welcome Back!</h5>
                                            <p className="text-muted">Sign in to continue to {COMPANY_NAME}</p>
                                        </div>

                                        <div className="p-2 mt-4">
                                            <Form
                                                onSubmit={(e) => {
                                                    e.preventDefault();
                                                    validation.handleSubmit();
                                                    return false;
                                                }}
                                            >
                                                <div className="mb-3">
                                                    <Label htmlFor="username" className="form-label">
                                                        Username or Email
                                                    </Label>
                                                    <Input
                                                        name="username"
                                                        className="form-control"
                                                        placeholder="Enter username or email"
                                                        type="text"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.username || ""}
                                                        invalid={
                                                            validation.touched.username && validation.errors.username ? true : false
                                                        }
                                                        disabled={loading}
                                                    />
                                                    {validation.touched.username && validation.errors.username ? (
                                                        <FormFeedback type="invalid">
                                                            {validation.errors.username}
                                                        </FormFeedback>
                                                    ) : null}
                                                </div>

                                                <div className="mb-3">
                                                    <Label className="form-label" htmlFor="password-input">
                                                        Password
                                                    </Label>
                                                    <div className="position-relative auth-pass-inputgroup mb-3">
                                                        <Input
                                                            name="password"
                                                            value={validation.values.password || ""}
                                                            type={passwordShow ? "text" : "password"}
                                                            className="form-control pe-5"
                                                            placeholder="Enter Password"
                                                            onChange={validation.handleChange}
                                                            onBlur={validation.handleBlur}
                                                            invalid={
                                                                validation.touched.password && validation.errors.password ? true : false
                                                            }
                                                            disabled={loading}
                                                        />
                                                        {validation.touched.password && validation.errors.password ? (
                                                            <FormFeedback type="invalid">
                                                                {validation.errors.password}
                                                            </FormFeedback>
                                                        ) : null}
                                                        <button
                                                            className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                                                            type="button"
                                                            id="password-addon"
                                                            onClick={() => setPasswordShow(!passwordShow)}
                                                            disabled={loading}
                                                        >
                                                            <i className={passwordShow ? "ri-eye-off-fill align-middle" : "ri-eye-fill align-middle"}></i>
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="mt-4">
                                                    <Button
                                                        color="primary"
                                                        disabled={loading}
                                                        className="btn btn-primary w-100"
                                                        type="submit"
                                                    >
                                                        {loading && (
                                                            <Spinner size="sm" className='me-2'>
                                                                Loading...
                                                            </Spinner>
                                                        )}
                                                        Sign In
                                                    </Button>
                                                </div>

                                                <div className="mt-4 text-center">
                                                    <p className="mb-0">
                                                        Don't have an account?{" "}
                                                        <Link
                                                            to="/register"
                                                            className="fw-semibold text-primary text-decoration-underline"
                                                        >
                                                            Signup
                                                        </Link>
                                                    </p>
                                                </div>
                                            </Form>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </div>
            </ParticlesAuth>
        </React.Fragment>
    );
};

export default withRouter(Login);